import { Box, Group, Space, Text, Stack, Button, Tooltip, Alert, Anchor } from '@mantine/core'
import { isHostZone } from '@/queries'
import { LsmValidator, useHostZoneQuery, useLsmValidatorsQuery } from '../queries'
import { assert } from '@/utils'
import { useStake } from '../StakeProvider'
import { LsmValidatorAvatar } from '../shared/LsmValidatorAvatar/LsmValidatorAvatar'
import { LsmValidatorName } from '../shared'
import { CHAIN_INFO_LIST } from '@/config'
import { formatMicroDenom } from '@/wallet-utils'
import { useSelectedDenom } from '../../../contexts/denom'
import { getLsmValidatorBlockStatus } from '../utils'

const StakeLsmSelection: React.FC = () => {
  const [denom] = useSelectedDenom()

  const chainInfo = CHAIN_INFO_LIST[denom]

  const { data: lsmValidators } = useLsmValidatorsQuery()

  const { data: hostZone } = useHostZoneQuery()

  assert(lsmValidators, `Unable to render LSM selection while validators have not loaded yet.`)

  assert(hostZone, `Unable to render LSM selection while hostZone has not loaded yet.`)

  assert(isHostZone(hostZone), `Incorrect host zone type for ${chainInfo.chainId}`)

  const lsmValidatorPayloads = lsmValidators.validators.map((validator) => {
    // Validate conditions ahead of time, and treat error handlers inside the LSM flows
    // as a fallback if for some unknown reason they're able to proceed. So to speak, if
    // we need to test error handling, we just need to comment out some conditions here.
    //
    // For validators not in the active set (not included in the hostZone.vaidators list),
    // I found that LSM Liquid Stake would end up with code -32603 error.
    //
    // For validators that were recently redelegated to, they'll run into simulation errors first
    // (which we handle) on the first step.
    //
    // For validators that have a remaining bond shares of 0, they'll run into validation errors first.
    // For testing, it will need to be disabled. BBut they'll immediately run into simulation and tx
    // errors on the first step (which we handle).
    const { message: tooltip } = getLsmValidatorBlockStatus({
      address: validator.address,
      hostZone,
      redelegating: validator.redelegating
    })

    return { validator, tooltip }
  })

  // Sort by amount from highest to lowest
  const toSortByAmount = (payloads: typeof lsmValidatorPayloads) => {
    return payloads.sort((a, b) => {
      const amounts = { a: BigInt(a.validator.amount), b: BigInt(b.validator.amount) }
      return amounts.a > amounts.b ? -1 : amounts.a < amounts.b ? 1 : 0
    })
  }

  // Sort validators without errors to the top
  const lsmValidatorsWithoutErrors = toSortByAmount(
    lsmValidatorPayloads.filter(({ tooltip }) => {
      return tooltip === ''
    })
  )

  // Sort validators with errors to the bottom
  const lsmValidatorsWithErrors = toSortByAmount(
    lsmValidatorPayloads.filter(({ tooltip }) => {
      return tooltip !== ''
    })
  )

  return (
    <>
      <Text inline sx={(t) => ({ color: t.colors.gray[7] })}>
        Choose the validator to pull from:
      </Text>

      <Space h="md" />

      <Stack spacing="lg">
        {lsmValidatorsWithoutErrors.map(({ validator, tooltip }) => {
          return <StakeLsmSelectionItem key={validator.address} validator={validator} tooltip={tooltip} />
        })}
        {lsmValidatorsWithErrors.map(({ validator, tooltip }) => {
          return <StakeLsmSelectionItem key={validator.address} validator={validator} tooltip={tooltip} />
        })}

        {lsmValidatorsWithErrors.length ? (
          <Alert color="yellow">
            <Text sx={(t) => ({ color: t.colors.gray[7] })}>
              If you can't select a validator of your choice, it likely doesn't meet Stride's requirements. Consider
              redelegating to another validator. Learn more{' '}
              <Anchor href="https://docs.stride.zone/docs/lsm-troubleshooting" target="_blank">
                here
              </Anchor>
              .
            </Text>
          </Alert>
        ) : null}
      </Stack>
    </>
  )
}

interface StakeLsmSelectionItemProps {
  validator: LsmValidator
  tooltip: string
}

const StakeLsmSelectionItem = ({ validator, tooltip }: StakeLsmSelectionItemProps) => {
  const [denom] = useSelectedDenom()

  const chainInfo = CHAIN_INFO_LIST[denom]

  const { setLsmValidatorAddress } = useStake()

  const handleSelect = () => {
    setLsmValidatorAddress(validator.address)
  }

  return (
    <Group position="apart" noWrap key={validator.address}>
      <Group spacing="sm" noWrap sx={{ minWidth: 0, opacity: tooltip ? 0.6 : 1 }}>
        <LsmValidatorAvatar address={validator.address} />

        <Box sx={{ minWidth: 0 }}>
          <Text inline weight={600} size="md" sx={(t) => ({ color: t.colors.gray[9] })}>
            <LsmValidatorName address={validator.address} limit={24} />
          </Text>

          <Space h="xs" />

          <Text inline sx={(t) => ({ color: t.colors.gray[7] })}>
            Available: {formatMicroDenom(validator.amount, chainInfo.stakeCurrency.coinMinimalDenom, 3)} {denom}
          </Text>
        </Box>
      </Group>

      <Tooltip label={tooltip} disabled={!tooltip} width={260} wrapLines>
        <Button onClick={handleSelect} disabled={Boolean(tooltip)}>
          Select
        </Button>
      </Tooltip>
    </Group>
  )
}

export { StakeLsmSelection }
